import { Component, Mixins, Watch } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import {
  Company,
  CompanyCollection,
  MasterCatalog,
  MasterCatalogCollection,
  PriceList,
  PriceListCollection,
  PriceListEntry
} from '@/firestore'
import VendorBarcodeScanDialog from '@/components/Barcode/VendorBarcodeScanDialog.vue'
import PhoneNumber from '@/components/PhoneNumber.vue'
import axios from 'axios'
import moment from 'moment'
import SaveButton from '@/components/SaveButton.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import CategorySelectionModal from '@/components/Order/CategorySelectionModal.vue'
import store, { Actions, Mutations } from '../../../store'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

@Component({
  components: {
    SaveButton,
    VendorBarcodeScanDialog,
    LoadingPage,
    PhoneNumber,
    LabeledValue,
    CategorySelectionModal,
    ConfirmationModal
  }
})
export default class PriceListEdit extends Mixins(IconsMixin, AuthMixin) {
  public priceList: PriceList | null = null
  public vendor: Company | null = null
  public vendorName: string = ''
  public masterCatalog: MasterCatalog = {} as MasterCatalog

  public selectedEntry: PriceListEntry = {} as PriceListEntry
  public errors: string[] = []

  public formValid: boolean = false
  public isSaving: boolean = false
  public showScrollTop: boolean = false
  public displayBarcodeScanner: boolean = false
  public displayMasterCatalogBarcodeScanner: boolean = false

  public showCacheConfirmation = false
  public setCacheConfirmation: (confirm: boolean) => void = () => {}

  public original: { priceList?: PriceList } = {}

  public $refs!: {
    form: HTMLFormElement
  }

  public onScroll() {
    this.showScrollTop = window.pageYOffset > 50
  }

  get isSuperUser() {
    // This is bad, we need to not do this
    return [
      // 'yH3qpeqTdHVYdOVgxoNaZJOu7fQ2', // Brandon Kilgoe
      // 'PaNwYY61R1OnLVGo1VIsvUskiTP2'  // Gabi Walker
      'lNPv52mgkKfIqQ3jrOExuxDbMM22'
    ].includes(this.user.uid)
  }

  get vendorCutoffTime() {
    const cutoffTime = this.priceList && this.priceList.cutoffTime ? this.priceList.cutoffTime : undefined
    return cutoffTime ? moment(cutoffTime).format('HH:mm') : ''
  }

  set vendorCutoffTime(value: string) {
    if (this.priceList) {
      this.priceList.cutoffTime = moment(value, 'HH:mm').toDate()
    }
  }

  public async mounted() {
    try {
      let priceList = await this.lookupPriceList(this.$route.params.id)
      if (!priceList) {
        const err = new Error('No Price List Found')
        return store.dispatch(Actions.SET_ERROR, [err, this.$router])
      }

      const cachedPriceList = store.state.cache.priceListEdit as PriceList
      if (cachedPriceList && cachedPriceList.id === priceList.id) {
        const confirmationResult = new Promise(res => {
          this.setCacheConfirmation = res
        })
        this.showCacheConfirmation = true
        if (await confirmationResult) {
          this.priceList = Object.assign(
            new PriceList(
              cachedPriceList.vendorCompanyId,
              cachedPriceList.restaurantCompanyId
            ),
            cachedPriceList
          )
        } else {
          this.priceList = priceList
          this.setPriceListCache()
        }
      } else {
        this.priceList = priceList
        this.setPriceListCache()
      }

      const masterCatalogCollection = new MasterCatalogCollection(( await this.$firebase ).firestore())
      const masterCatalog = await masterCatalogCollection.getByCompanyId(this.priceList.vendorCompanyId)
      if(masterCatalog) {
        this.masterCatalog = masterCatalog
      }

      const companycollection = new CompanyCollection(( await this.$firebase ).firestore())
      const vendor = await companycollection.getCompany(this.priceList.vendorCompanyId)
      if(vendor) {
        this.vendor = vendor
      }

      this.vendorName = (this.vendor && this.vendor.displayName)
        ? this.vendor.displayName
        : this.priceList.vendorCompanyName

      window.addEventListener('scroll', this.onScroll);

      this.original.priceList = { ...this.priceList } as PriceList

      if(!this.priceList.entries || !this.priceList.entries.length) {
        this.addEntry()
      }
    } catch (error) {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
  }

  public addEntry() {
    if (!this.priceList) {
      return
    }

    const entry = new PriceListEntry('', '')
    this.priceList.entries.unshift(entry)
  }

  public addMasterCatalogEntry() {
    if (!this.masterCatalog || !this.masterCatalog.id) {
      if (this.vendor && this.vendor.id) {
        this.masterCatalog = new MasterCatalog(this.vendor.id)
      } else {
        return
      }
    }

    const entry = new PriceListEntry('', '')
    this.masterCatalog.entries.unshift(entry)
  }

  public addScanEntry(entry: PriceListEntry) {
    if (this.displayMasterCatalogBarcodeScanner) {
      if (!this.masterCatalog || !this.masterCatalog.id) {
        if (this.vendor && this.vendor.id) {
          this.masterCatalog = new MasterCatalog(this.vendor.id)
        } else {
          return
        }
      }

      this.removeEmptyEntries()
      this.masterCatalog.entries.push(entry)
    } else {
      if (!this.priceList) {
        return
      }

      this.removeEmptyEntries()
      this.priceList.entries.push(entry)
    }
  }

  public addMasterCatalogScanEntry(entry: PriceListEntry) {
    if (!this.masterCatalog || !this.masterCatalog.id) {
      if (this.vendor && this.vendor.id) {
        this.masterCatalog = new MasterCatalog(this.vendor.id)
      } else {
        return
      }
    }

    this.removeEmptyEntries()
    this.masterCatalog.entries.push(entry)
  }

  public removeEntry(entry: PriceListEntry) {
    if (!this.priceList) {
      return
    }

    const index = this.priceList.entries.indexOf(entry)
    if (index !== -1) {
      this.priceList.entries.splice(index, 1)
    }
  }

  public removeMasterCatalogEntry(entry: PriceListEntry) {
    if (!this.masterCatalog || !this.masterCatalog.id) {
      return
    }

    const index = this.masterCatalog.entries.indexOf(entry)
    if (index !== -1) {
      this.masterCatalog.entries.splice(index, 1)
    }
  }

  public getCategory(entry: PriceListEntry) {
    if(!entry || !entry.category) {
      return 'N/A'
    }

    const lineageArray = entry.category.split(' > ')
    return lineageArray[lineageArray.length - 1].trim()
  }

  public scrollToTop() {
    window.scrollTo(0,0);
  }

  public isPriceListValid(priceList: PriceList | null): priceList is PriceList {
    if(!priceList) {
      return false
    }

    if(!this.$refs.form.validate()) {
      if(!priceList.vendorRepName) {
        this.errors.push("Please provide the vendor rep name")
      }
      if(!priceList.vendorRepPhoneNumber) {
        this.errors.push("Please provide the vendor rep phone number")
      }
      if(
        !/^([A-Za-z0-9_+\-.])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,4})$/.test(
          priceList.vendorRepEmail
        )
      ) {
        this.errors.push("Invalid email format")
      }
      if(!priceList.entries.every(entry => entry.description)) {
        this.errors.push("You must enter a description for every item")
      }
    }

    if(!priceList.entries.every(entry => entry.category)) {
      this.errors.push("Please select a category for every entry")
      return false
    }

    return !!this.$refs.form.validate()
      && priceList instanceof PriceList
      && !this.errors.length
  }

  public async updatePriceList() {
    if (this.priceList) {
      const priceListCollection = new PriceListCollection(( await this.$firebase ).firestore())
      await priceListCollection.save({
        ...this.priceList,
        entries: this.priceList.entries.filter(entry => !entry.isBlank)
      } as PriceList)
    }
  }

  public async updateMasterCatalog() {
    if (this.masterCatalog && this.masterCatalog.entries && this.masterCatalog.entries.length) {
      const masterCatalogCollection = new MasterCatalogCollection(( await this.$firebase ).firestore())
      await masterCatalogCollection.save({
        ...this.masterCatalog,
        entries: this.masterCatalog.entries.filter(entry => !entry.isBlank)
      } as MasterCatalog)
    }
  }

  @Watch('priceList', { deep: true })
  public priceListChanges(priceList: PriceList | null, previousPriceList: PriceList | null) {
    if (priceList && previousPriceList) {
      this.setPriceListCache(priceList)
    }
  }

  public setPriceListCache(priceList: PriceList | null = null) {
    store.commit(Mutations.UPDATE_CACHE_PRICELIST_EDIT, { priceList })
  }

  public async save() {
    this.errors = []
    this.removeEmptyEntries()

    if (!this.isPriceListValid(this.priceList) || !this.priceList.vendorCompanyId) {
      return
    }

    this.isSaving = true

    await this.updateVendorEmail()
    await this.updatePriceList()

    if (this.isSuperUser) {
      await this.updateMasterCatalog()
    }

    await this.back()
    this.isSaving = false
  }

  public back() {
    this.setPriceListCache()
    return this.vendor
      ? this.$router.push({ name: 'vendor-details', params: { id: this.vendor.id } })
      : this.$router.push({ name: 'vendor-list' })
  }

  protected async updateVendorEmail() {
    if (!this.priceList || !this.priceList.vendorCompanyId) {
      return
    }

    if (!this.original.priceList
      || this.original.priceList.vendorRepEmail != this.priceList.vendorRepEmail
      || this.original.priceList.vendorRepPhoneNumber != this.priceList.vendorRepPhoneNumber) {
      const response = await axios.post(
        `/api/vendors/${ this.priceList.vendorCompanyId }/rep`,
        { email: this.priceList.vendorRepEmail, phoneNumber: this.priceList.vendorRepPhoneNumber }
      ) as {data: { userId: string, userEmail: string, userPhoneNumber: string } }

      this.priceList.vendorRepId = response.data.userId
    }
  }

  private removeEmptyEntries() {
    // Backwards loop through all priceListEntries and remove all blank entries
    if(this.priceList && this.priceList.entries && this.priceList.entries.length > 0) {
      for (let index = this.priceList.entries.length - 1; index >= 0; index--) {
        if (this.priceList.entries[index].isBlank) {
          this.removeEntry(this.priceList.entries[index])
        }
      }
    }

    if (this.isSuperUser) {
      // Backwards loop through all masterCatalogEntries and remove all blank entries
      if(this.masterCatalog && this.masterCatalog.entries && this.masterCatalog.entries.length > 0) {
        for (let index = this.masterCatalog.entries.length - 1; index >= 0; index--) {
          if (this.masterCatalog.entries[index].isBlank) {
            this.removeMasterCatalogEntry(this.masterCatalog.entries[index])
          }
        }
      }
    }
  }

  private async lookupPriceList(id: string) {
    const priceListCollection = new PriceListCollection(( await this.$firebase ).firestore())
    return await priceListCollection.get(id)
  }
}
